import {
  BASEMAP_COARSEST_LEVEL,
  BasemapDraftDocument,
  BasemapGeometry,
  BasemapManifest,
  BasemapName,
  Sha256,
} from "@pax/basemap";
import { z } from "zod";
import { AssetId } from "../../assets/v1alpha1/schemas";
import { Timestamp } from "../../common";

const EXAMPLE = {
  assetId: "019a0f3c-7d21-7c4e-9a55-2f61e8b0c4d7",
  versionId: "019a0f3c-7d22-7b19-8e40-c3a5d61f0b28",
  copiedVersionId: "0199e4b2-11c8-7a03-b6de-5c0a9f37e812",
  manifest: "4be1c0d27f9a83e65b10c4d9f2a7e38c61b05d4a9e2f7c13b8d60a5e4f91c2b7",
  expiresAt: "2026-10-23T22:46:07.000Z",
} as const;

/** The publish Workflow's steps, in order. A running publish is on one of them. */
export const BASEMAP_PUBLISH_STEPS = [
  "freeze",
  "check_document",
  "bundle",
  "write_manifest",
  "check_package",
  "screen",
  "publish",
] as const;
export type BasemapPublishStep = (typeof BASEMAP_PUBLISH_STEPS)[number];
/** The largest draft blob: an uploaded tile file, which one presigned PUT can carry. */
export const BASEMAP_BLOB_MAX_BYTES = 5 * 1024 ** 3;
/** The finest level a new world can start at, about 4.9 km a pixel on Earth. */
export const BASEMAP_NEW_WORLD_FINEST_LEVEL = 11;

export const AssetVersionId = z.uuid().meta({
  description: "An asset version's id: a draft, or the published version it became.",
  example: EXAMPLE.versionId,
});

export const BasemapParams = z
  .strictObject({ id: AssetId })
  .meta({ id: "BasemapParams", description: "A route parameter naming one basemap asset." });

export const BasemapQuery = z
  .strictObject({
    version: AssetVersionId.optional().meta({
      description: "The published version to read. Without it, the asset's live version.",
    }),
  })
  .meta({ id: "BasemapQuery" });

export const BasemapDraftParams = BasemapParams.extend({ versionId: AssetVersionId }).meta({
  id: "BasemapDraftParams",
  description: "A basemap asset, and one of its drafts.",
});

export const BasemapManifestParams = z
  .strictObject({ sha256: Sha256 })
  .meta({ id: "BasemapManifestParams", description: "A route parameter naming one manifest." });

export const BasemapFileParams = z
  .strictObject({ sha256: Sha256 })
  .meta({ id: "BasemapFileParams", description: "A route parameter naming one published file." });

export const BasemapBlobParams = BasemapDraftParams.extend({ sha256: Sha256 }).meta({
  id: "BasemapBlobParams",
  description: "A draft, and one of its blobs.",
});

export const BasemapBlobHeaders = z
  .object({
    range: z
      .string()
      .regex(/^bytes=\d+-\d+$/)
      .optional()
      .meta({
        description:
          "One range of the blob's bytes, `bytes=start-end`, such as an uploaded tile file's header and directories.",
        example: "bytes=0-16383",
      }),
  })
  .meta({ id: "BasemapBlobHeaders" });

export const BasemapJobParams = BasemapParams.extend({
  jobId: z
    .string()
    .regex(/^publish-[0-9a-f-]{36}-\d+$/)
    .meta({
      description:
        "The job's Workflow instance: the draft's version id, and the commit it froze at.",
      example: `publish-${EXAMPLE.versionId}-42`,
    }),
}).meta({ id: "BasemapJobParams" });

// Starting drafts

export const CreateBasemapRequest = z
  .strictObject({
    from: z.discriminatedUnion("kind", [
      z
        .strictObject({
          kind: z.literal("new"),
          geometry: BasemapGeometry,
          level: z.int().min(BASEMAP_COARSEST_LEVEL).max(BASEMAP_NEW_WORLD_FINEST_LEVEL).meta({
            description: "The level its `cover` layer starts at.",
            example: 10,
          }),
        })
        .meta({ description: "A new basemap whose room holds a blank standard world." }),
      z
        .strictObject({
          kind: z.literal("version"),
          assetId: AssetId,
          versionId: AssetVersionId.meta({ description: "The published version to start from." }),
        })
        .meta({
          description:
            "A new draft of the same basemap, based on one of its published versions. Only its owner and collaborators may.",
        }),
      z
        .strictObject({
          kind: z.literal("copy"),
          versionId: AssetVersionId.meta({ description: "The published version to copy." }),
        })
        .meta({
          description:
            "A new basemap of the caller's own, whose room's base is the version's manifest, and whose manifest names it in `remixOf`.",
        }),
      z
        .strictObject({
          kind: z.literal("package"),
          manifest: BasemapManifest.meta({
            description:
              "The uploaded package's manifest, which the editor has checked with `checkPackage`.",
          }),
          files: z
            .array(
              z.strictObject({ sha256: Sha256, bytes: z.int().min(1).max(BASEMAP_BLOB_MAX_BYTES) }),
            )
            .min(1)
            .max(64)
            .meta({
              description:
                "Every file the manifest names, and its size. The response says where to upload each.",
            }),
        })
        .meta({ description: "A new basemap whose room's base is an uploaded package." }),
    ]),
  })
  .meta({
    id: "CreateBasemapRequest",
    description: "Where a basemap draft starts.",
    example: { from: { kind: "copy", versionId: EXAMPLE.copiedVersionId } },
  });

// Published basemaps

const BasemapFile = z
  .strictObject({
    name: BasemapName.meta({
      description: "The bundle or feature set the manifest lists it under.",
    }),
    role: z.enum(["bundle", "features"]),
    sha256: Sha256,
    bytes: z.int().min(1),
    removed: z.boolean().meta({
      description: "Whether staff deleted its bytes, which leaves every basemap listing it broken.",
    }),
    tiles: z.string().nullable().meta({
      description:
        "Where the tiles worker serves its tiles while the basemap is published, with `{z}`, `{x}`, and `{y}` for a tile's PMTiles address: `.avif` for a color raster encoded as AVIF, `.json` for a feature set, and `.webp` for everything else. Never changed. Null while a moderator has taken the basemap down, when its files have no public copies.",
      example:
        "https://tiles.paxusercontent.co/assets/basemaps/earth/1/bundles/cover.pmtiles/{z}/{x}/{y}.webp",
    }),
  })
  .extend({
    url: z.url().nullable().meta({
      description:
        "For the owner of a basemap a moderator took down, a signed URL that reads the whole file, by ranges, until `urlsExpireAt`. Null otherwise.",
    }),
  })
  .meta({ id: "BasemapFile" });

export const BasemapResponse = z
  .strictObject({
    assetId: AssetId,
    versionId: AssetVersionId,
    version: z.int().min(1).meta({
      description: "Its number among the basemap's published versions, 1 for the first.",
    }),
    removed: z.boolean().meta({
      description: "Whether a moderator took the version down. Its manifest stops being public.",
    }),
    assetRemoved: z.boolean().meta({
      description:
        "Whether a moderator took the whole basemap down. Only its owner can read it then, through signed URLs.",
    }),
    urlsExpireAt: Timestamp.nullable().meta({
      description: "When the signed URLs of a taken-down basemap stop working, or null.",
    }),
    manifest: z.strictObject({
      sha256: Sha256,
      url: z.url().meta({
        description:
          "Where the manifest is served while the version is published, never changed; for the owner of a taken-down basemap, a signed URL.",
        example: "https://paxusercontent.co/assets/basemaps/earth/1/manifest.json",
      }),
      bytes: z.int().min(1),
      compressedBytes: z.int().min(1).max(65_536),
      format: z.string().meta({ example: "pax-world-v1" }),
    }),
    geometry: z.enum(["sphere", "plane"]),
    remixOf: z
      .strictObject({ assetId: AssetId, versionId: AssetVersionId, manifestSha256: Sha256 })
      .nullable()
      .meta({ description: "The version it was copied from, for attribution." }),
    files: z.array(BasemapFile).meta({
      description: "Every file the manifest lists, shared ones included.",
    }),
    publishedAt: Timestamp,
  })
  .meta({
    id: "BasemapResponse",
    description:
      "A published basemap version: its manifest and files. Its title, description, and listing come from the Assets API.",
  });

// Drafts

const PresignedPut = z
  .strictObject({
    url: z.url().meta({
      description: "A presigned storage URL. Send the bytes to it as the request body.",
    }),
    method: z.literal("PUT"),
    headers: z.record(z.string(), z.string()).meta({ description: "Send exactly these headers." }),
    expiresAt: Timestamp.meta({ example: EXAMPLE.expiresAt }),
  })
  .meta({ id: "BasemapPresignedPut" });

export const BasemapDraftResponse = z
  .strictObject({
    assetId: AssetId,
    versionId: AssetVersionId.meta({ description: "The draft's id, which also names its room." }),
    status: z.enum(["draft", "publishing", "refused", "in_appeal"]),
    seq: z.int().min(0).meta({
      description: "How many commits the draft's room has taken. Send it as `seq` to publish.",
      example: 42,
    }),
    createdFromVersionId: AssetVersionId.nullable().meta({
      description:
        "The published version the draft was created from, of this basemap or, for a copy, another. Null for one made from scratch.",
    }),
    document: BasemapDraftDocument,
    baseTiles: z.record(Sha256, z.string()).meta({
      description:
        "Where the tiles worker serves each published tile file a raster's base reads from, by its hash, as a URL template like a published file's `tiles`. A base file uploaded into the draft is read as a blob instead.",
    }),
    changedTiles: z.int().min(0),
    storedBytes: z.int().min(0).meta({
      description: "What the blobs the draft holds take, which count toward its author's storage.",
    }),
    manifestCompressedBytes: z.int().min(0).meta({
      description:
        "How large the manifest would be, gzipped, if the draft were published now. Publishing refuses past 65,536.",
    }),
    publishing: z.string().nullable().meta({
      description:
        "The draft's publish job while it runs, or null. The draft takes no commits while it runs.",
    }),
    problems: z
      .array(z.strictObject({ where: z.string(), message: z.string() }))
      .nullable()
      .meta({ description: "Why its last publish was refused or failed, or null." }),
    uploads: z
      .array(
        z.strictObject({
          sha256: Sha256,
          upload: PresignedPut,
        }),
      )
      .meta({
        description:
          "For a draft started from an uploaded package, where to upload each file its manifest names. Keep them once they're uploaded. Empty otherwise.",
      }),
    updatedAt: Timestamp,
  })
  .meta({
    id: "BasemapDraftResponse",
    description: "A draft as it is: its version row, and its room's document and commit count.",
  });

export const CreateBasemapBlobsRequest = z
  .strictObject({
    blobs: z
      .array(z.strictObject({ sha256: Sha256, bytes: z.int().min(1).max(BASEMAP_BLOB_MAX_BYTES) }))
      .min(1)
      .max(64),
  })
  .meta({
    id: "CreateBasemapBlobsRequest",
    description: "Blobs the client is about to upload, named by their hashes.",
  });

export const CreateBasemapBlobsResponse = z
  .strictObject({
    uploads: z.array(z.strictObject({ sha256: Sha256, upload: PresignedPut })).meta({
      description:
        "Where to upload each blob the draft doesn't hold yet. A blob it already holds is left out.",
    }),
  })
  .meta({ id: "CreateBasemapBlobsResponse" });

export const KeepBasemapBlobsRequest = z
  .strictObject({ blobs: z.array(Sha256).min(1).max(4096) })
  .meta({ id: "KeepBasemapBlobsRequest", description: "The blobs to keep, by their hashes." });

// Publishing

const PackageProblem = z
  .strictObject({
    where: z.string().meta({ example: "cover, face 2, level 12, tile 40 / 17" }),
    message: z.string().meta({ example: "Id 40 isn't in cover's legend." }),
  })
  .meta({ id: "BasemapPackageProblem", description: "One way a package isn't a basemap." });

export const BasemapJob = z
  .strictObject({
    id: z.string().meta({ example: `publish-${EXAMPLE.versionId}-42` }),
    kind: z.literal("publish"),
    seq: z.int().min(0).meta({ description: "The commit the draft's room froze at." }),
    status: z.enum(["queued", "running", "succeeded", "failed"]),
    step: z.enum(BASEMAP_PUBLISH_STEPS).nullable().meta({
      description:
        "The step a running publish is on, which each step records as it begins, or null before the first and once it's done.",
    }),
    failure: z
      .strictObject({
        reason: z.enum(["draft_invalid", "package_invalid", "refused", "internal"]),
        message: z.string(),
        problems: z.array(PackageProblem).meta({
          description: "Every problem `checkPackage` found, for a package that isn't a basemap.",
        }),
      })
      .nullable(),
    result: z
      .strictObject({ versionId: AssetVersionId, manifestSha256: Sha256 })
      .nullable()
      .meta({ description: "The published version, the draft itself, and its manifest." }),
  })
  .meta({
    id: "BasemapJob",
    description: "A Cloudflare Workflow publishing one commit of a draft.",
  });

export const PublishBasemapRequest = z
  .strictObject({
    seq: z.int().min(0).meta({
      description: "The commit to publish, which must be the room's latest.",
      example: 42,
    }),
    label: z.string().trim().min(1).max(40).optional().meta({
      description: "An optional name for the version, such as `Winter`.",
    }),
    attestsCopyright: z.literal(true).meta({
      description: "The author confirms the basemap violates no copyrights or trademarks.",
    }),
  })
  .meta({ id: "PublishBasemapRequest" });

// Takedowns

export const BasemapFileResponse = z
  .strictObject({
    sha256: Sha256,
    role: z.enum(["bundle", "features"]),
    bytes: z.int().min(1),
    removedAt: Timestamp.nullable(),
    basemaps: z
      .array(z.strictObject({ assetId: AssetId, versionId: AssetVersionId, name: BasemapName }))
      .meta({
        description: "Every basemap version that lists it, and the name it lists it under.",
      }),
  })
  .meta({ id: "BasemapFileResponse" });

export const DeleteBasemapFileRequest = z
  .strictObject({ reason: z.string().min(1).max(500) })
  .meta({ id: "DeleteBasemapFileRequest" });
